/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import Dialog, { Callout } from '../Dialog';
import { BillingPeriod, GPUPricing, ResourcePricing } from '../../../types';
import { PodForm } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  form: PodForm;
  selectedGPU: GPUPricing | null;
  getResourcePricing: (type: string) => ResourcePricing | undefined;
  includedCpu: number;
  includedMemory: number;
}

const PERIODS: { value: BillingPeriod; label: string; multiplier: number }[] = [
  { value: 'minute', label: 'Per minute', multiplier: 1 },
  { value: 'hour', label: 'Per hour', multiplier: 60 },
  { value: 'day', label: 'Per day', multiplier: 1440 },
  { value: 'month', label: 'Per month', multiplier: 43800 },
  { value: 'quarter', label: 'Per quarter', multiplier: 131400 },
  { value: 'annual', label: 'Per year', multiplier: 525600 },
];

const PriceComparisonDialog: React.FC<Props> = ({
  open,
  onClose,
  form,
  selectedGPU,
  getResourcePricing,
  includedCpu,
  includedMemory,
}) => {
  const rows = PERIODS.map((period) => {
    const cpuPricing = getResourcePricing('cpu');
    const memoryPricing = getResourcePricing('memory');
    const gpuPricing = selectedGPU;
    const extraCPU = Math.max(0, form.cpu_cores - includedCpu);
    const extraMemory = Math.max(0, form.memory_gb - includedMemory);

    const discountOf = (pricing: any): number => {
      if (!pricing) return 0;
      switch (period.value) {
        case 'hour': return pricing.hourly_discount || 0;
        case 'day': return pricing.daily_discount || 0;
        case 'month': return pricing.monthly_discount || 0;
        case 'quarter': return pricing.quarter_discount || 0;
        case 'annual': return pricing.annual_discount || 0;
        default: return 0;
      }
    };

    const cpuRate = cpuPricing ? cpuPricing.price_per_unit_per_minute * (1 - discountOf(cpuPricing) / 100) : 0;
    const memoryRate = memoryPricing ? memoryPricing.price_per_unit_per_minute * (1 - discountOf(memoryPricing) / 100) : 0;
    const gpuRate = gpuPricing ? gpuPricing.price_per_minute * (1 - discountOf(gpuPricing) / 100) : 0;

    const cpuCost = extraCPU * cpuRate * period.multiplier;
    const memoryCost = extraMemory * memoryRate * period.multiplier;
    const gpuCost = selectedGPU ? form.gpu_count * gpuRate * period.multiplier : 0;
    const totalCost = (cpuCost + memoryCost + gpuCost) * form.replicas;
    const costPerMinute = totalCost / period.multiplier;

    const minuteRate = (() => {
      const cpuR = cpuPricing ? cpuPricing.price_per_unit_per_minute : 0;
      const memR = memoryPricing ? memoryPricing.price_per_unit_per_minute : 0;
      const gpuR = gpuPricing ? gpuPricing.price_per_minute : 0;
      return (extraCPU * cpuR + extraMemory * memR + (selectedGPU ? form.gpu_count * gpuR : 0)) * form.replicas;
    })();
    const savings = minuteRate * period.multiplier - totalCost;
    const discountPercent = discountOf(gpuPricing || cpuPricing);
    return { period, totalCost, savings, discountPercent, costPerMinute };
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Pricing comparison & savings"
      size="xl"
      footer={
        <button type="button" className="ps-btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <Callout tone="info" title="Your configuration">
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {selectedGPU && (
              <span>
                GPU: {form.gpu_count}× {form.gpu_type}
              </span>
            )}
            <span>vCPU: {form.cpu_cores}</span>
            <span>Memory: {form.memory_gb} GB</span>
            <span>Replicas: {form.replicas}</span>
          </div>
        </Callout>
        <div className="ps-card overflow-x-auto">
          <table className="ps-tbl">
            <thead>
              <tr>
                <th>Billing period</th>
                <th className="!text-right">Total cost</th>
                <th className="!text-right">Discount</th>
                <th className="!text-right">You save</th>
                <th className="!text-right">Cost per minute</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ period, totalCost, savings, discountPercent, costPerMinute }) => {
                const on = form.billing_period === period.value;
                return (
                  <tr key={period.value} className={on ? '[&>td]:!bg-ps-accent-soft' : undefined}>
                    <td className={on ? 'text-ps-accent font-semibold' : ''}>
                      {period.label}
                      {on && ' ✓'}
                    </td>
                    <td className="text-right font-semibold">₹{totalCost.toFixed(2)}</td>
                    <td className={`text-right ${discountPercent > 0 ? 'text-ps-ok' : 'text-ps-subtle'}`}>
                      {discountPercent > 0 ? `${discountPercent}%` : '-'}
                    </td>
                    <td className={`text-right ${savings > 0 ? 'text-ps-ok' : 'text-ps-subtle'}`}>
                      {savings > 0 ? `₹${savings.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-right text-ps-muted">₹{costPerMinute.toFixed(4)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="m-0 text-xs text-ps-subtle">
          Base rates before taxes &amp; fees. Longer billing periods carry higher discounts where configured.
        </p>
      </div>
    </Dialog>
  );
};

export default PriceComparisonDialog;
